import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { AuthApiError, } from "@onlooker/auth-core";
import { createContext, useContext } from "react";
import { getTokenExpiration as defaultGetTokenExpiration } from "./jwt";
import { computeExpirySchedule } from "./schedule";
import { isSessionGone, parseRetryAfter, retryDelayMs } from "./transient";
export function createLocalStorageTokenStorage(key = "auth_token", storage = typeof window !== "undefined"
    ? window.localStorage
    : undefined) {
    return {
        getToken: () => {
            if (!storage)
                return null;
            try {
                return storage.getItem(key);
            }
            catch {
                return null;
            }
        },
        setToken: (token) => {
            if (!storage)
                return;
            try {
                storage.setItem(key, token);
            }
            catch {
                // Silently fail if storage is full or unavailable
            }
        },
        clearToken: () => {
            if (!storage)
                return;
            try {
                storage.removeItem(key);
            }
            catch {
                // Silently fail
            }
        },
    };
}
export function createAuthApiClient(options) {
    const fetchImpl = options.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : undefined);
    const baseUrl = options.baseUrl ?? "";
    if (!fetchImpl) {
        throw new Error("fetch is not available—provide fetchImpl in options");
    }
    async function request(method, path, body, init = {}) {
        const token = options.tokenStorage.getToken();
        const headers = {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...init.headers,
        };
        const response = await fetchImpl(`${baseUrl}${path}`, {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
            ...init,
        });
        const data = await response.json().catch(() => ({}));
        if (response.status === 401) {
            options.tokenStorage.clearToken();
            options.onUnauthorized?.();
            throw new AuthApiError(401, "unauthorized", "Session expired");
        }
        if (!response.ok) {
            // apps/api wraps every error through a shared errorHandler as
            // { success: false, error: { code, message, details } }. Reading
            // `data.error` as the code - which this did - yields an OBJECT
            // against the real API and a string against a mock, so a check like
            // `err.code === "not_found"` passed in development and failed in
            // production. The narrow type below is deliberate: the `as any` that
            // used to be here is what let the two shapes go unnoticed.
            const envelope = (data ?? {});
            throw new AuthApiError(response.status, typeof envelope.error?.code === "string"
                ? envelope.error.code
                : "unknown_error", envelope.error?.message ??
                `Request failed with status ${response.status}`, envelope.error?.details, parseRetryAfter(response.headers.get("Retry-After"), Date.now()));
        }
        return data;
    }
    return {
        request,
        get(path, init) {
            return request("GET", path, undefined, init);
        },
        post(path, body, init) {
            return request("POST", path, body, init);
        },
        patch(path, body, init) {
            return request("PATCH", path, body, init);
        },
        delete(path, init) {
            return request("DELETE", path, undefined, init);
        },
    };
}
// ============================================================================
// React Auth Factory
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { Navigate, useLocation } from "react-router-dom";
export function createReactAuth(options) {
    const AuthContext = createContext(null);
    const getExpiration = options.getTokenExpiration ?? defaultGetTokenExpiration;
    const autoRefreshLeadMs = options.autoRefreshLeadMs ?? 60_000;
    const expiryWarningLeadMs = options.expiryWarningLeadMs ?? 5 * 60_000;
    const tokenStorageKey = options.tokenStorageKey ?? "auth_token";
    /** Expiry (ms epoch) of whatever token is currently in storage. */
    function currentTokenExpiry() {
        const token = options.tokenStorage.getToken();
        return token ? getExpiration(token) : null;
    }
    function resolveStorageTarget() {
        if (options.storageEventTarget !== undefined) {
            return options.storageEventTarget;
        }
        return typeof window !== "undefined" ? window : null;
    }
    // Bridges the app's transport layer to the live provider's local logout.
    // The mounted provider populates this; `expireSession` (on the returned
    // object) calls through it so app code outside React — e.g. an API client's
    // terminal-401 handler — can trigger a session reset without a hook.
    let requestLocalLogout = null;
    function expireSession() {
        requestLocalLogout?.();
    }
    function useAuth() {
        const context = useContext(AuthContext);
        if (!context) {
            throw new Error("useAuth must be used within AuthProvider");
        }
        return context;
    }
    function useAuthState() {
        const initialExtraState = useMemo(() => options.initialState, []);
        const [state, setState] = useState(() => {
            const hasToken = Boolean(options.tokenStorage.getToken());
            return {
                user: null,
                loading: hasToken,
                error: null,
                unavailable: null,
                sessionExpiresAt: hasToken ? currentTokenExpiry() : null,
                sessionExpiringSoon: false,
                ...initialExtraState,
            };
        });
        const setPartialState = useCallback((partial) => {
            setState((current) => ({ ...current, ...partial }));
        }, []);
        const applySession = useCallback((session) => {
            const { user, ...extra } = session;
            setState((current) => ({
                ...current,
                ...extra,
                user,
                loading: false,
                error: null,
                unavailable: null,
                sessionExpiresAt: currentTokenExpiry(),
                sessionExpiringSoon: false,
            }));
        }, []);
        const resetState = useCallback(() => {
            setState({
                ...initialExtraState,
                user: null,
                loading: false,
                error: null,
                unavailable: null,
                sessionExpiresAt: null,
                sessionExpiringSoon: false,
            });
        }, [initialExtraState]);
        // --- ONL-243: a session that is unreachable, not gone -------------------
        // One timer for hydrate, the proactive refresh and the cross-tab
        // re-hydrate: whichever retry is pending, a newer one supersedes it.
        const retryTimer = useRef(null);
        const failures = useRef(0);
        const mounted = useRef(true);
        const clearRetry = useCallback(() => {
            if (retryTimer.current) {
                clearTimeout(retryTimer.current);
                retryTimer.current = null;
            }
        }, []);
        useEffect(() => {
            mounted.current = true;
            return () => {
                mounted.current = false;
                clearRetry();
            };
        }, [clearRetry]);
        // A failure that leaves no token behind is a sign-out, whatever was
        // thrown: the web app's refreshSession throws a plain Error both when the
        // refresh was rejected (and it cleared the tokens) and when it was only
        // unreachable.
        const sessionGone = useCallback((error) => isSessionGone(error) || !options.tokenStorage.getToken(), []);
        const scheduleRetry = useCallback((error, rerun, visible) => {
            const delay = retryDelayMs(error, failures.current);
            failures.current += 1;
            clearRetry();
            retryTimer.current = setTimeout(() => {
                retryTimer.current = null;
                rerun();
            }, delay);
            if (visible) {
                setPartialState({
                    loading: false,
                    unavailable: { retryAt: Date.now() + delay },
                });
            }
        }, [clearRetry, setPartialState]);
        const succeed = useCallback((session) => {
            failures.current = 0;
            clearRetry();
            applySession(session);
        }, [applySession, clearRetry]);
        const clearLocalSession = useCallback(() => {
            failures.current = 0;
            clearRetry();
            options.tokenStorage.clearToken();
            options.onLogoutCleanup?.();
            resetState();
        }, [clearRetry, resetState]);
        // --- Task 3.3: logout with API-first cleanup ---------------------------
        // Call the logout endpoint while the token is still present so the server
        // can invalidate the session, THEN clear local storage and run extra
        // cleanup (refresh token, in-flight request abort). `callApi: false` is
        // used for reactive logouts (failed refresh, cross-tab) where hitting the
        // endpoint again would be redundant or loop.
        const performLogout = useCallback(async ({ callApi }) => {
            failures.current = 0;
            clearRetry();
            if (callApi) {
                try {
                    await options.logout?.();
                }
                catch {
                    // Best-effort: never block local cleanup on a failed API call.
                }
            }
            options.tokenStorage.clearToken();
            options.onLogoutCleanup?.();
            resetState();
        }, [clearRetry, resetState]);
        const logout = useCallback(() => performLogout({ callApi: true }), [performLogout]);
        const refresh = useCallback(async () => {
            try {
                const session = options.refreshSession
                    ? await options.refreshSession()
                    : await options.loadSession();
                succeed(session);
            }
            catch (error) {
                if (!mounted.current)
                    return;
                // Task 3.2: a refresh the server refused forces re-login. Local-only
                // cleanup, since the refusal already proved the session is gone.
                if (sessionGone(error)) {
                    await performLogout({ callApi: false });
                    return;
                }
                // Anything else: the person is signed in and the access token is still
                // good for a while, so retry quietly (ONL-243).
                scheduleRetry(error, () => void refreshRef.current(), false);
            }
        }, [succeed, sessionGone, performLogout, scheduleRetry]);
        const refreshRef = useRef(refresh);
        refreshRef.current = refresh;
        // --- Task 3.1: session hydration on app load ---------------------------
        const hydrate = useCallback(async () => {
            const token = options.tokenStorage.getToken();
            if (!token)
                return;
            const expiresAt = getExpiration(token);
            const expired = expiresAt !== null && Date.now() >= expiresAt;
            // Token already expired with no way to refresh it: nothing to retry.
            if (expired && !options.refreshSession) {
                clearLocalSession();
                return;
            }
            try {
                const session = expired && options.refreshSession
                    ? await options.refreshSession()
                    : await options.loadSession();
                if (mounted.current)
                    succeed(session);
            }
            catch (error) {
                if (!mounted.current)
                    return;
                if (sessionGone(error)) {
                    clearLocalSession();
                    return;
                }
                scheduleRetry(error, () => void hydrateRef.current(), true);
            }
        }, [clearLocalSession, succeed, sessionGone, scheduleRetry]);
        const hydrateRef = useRef(hydrate);
        hydrateRef.current = hydrate;
        useEffect(() => {
            void hydrateRef.current();
        }, []);
        const retrySession = useCallback(() => {
            clearRetry();
            void hydrateRef.current();
        }, [clearRetry]);
        // --- Task 3.2: expiry warning + proactive auto-refresh -----------------
        const sessionExpiresAt = state.sessionExpiresAt;
        const sessionExpiringSoon = state.sessionExpiringSoon;
        const isAuthenticated = Boolean(state.user);
        useEffect(() => {
            if (sessionExpiresAt === null || !isAuthenticated)
                return;
            const { warnInMs, refreshInMs } = computeExpirySchedule(sessionExpiresAt, Date.now(), {
                autoRefreshLeadMs,
                expiryWarningLeadMs,
                alreadyWarned: sessionExpiringSoon,
            });
            const timers = [];
            if (warnInMs !== null) {
                const fireWarning = () => {
                    setState((current) => ({
                        ...current,
                        sessionExpiringSoon: true,
                    }));
                    options.onSessionExpiringSoon?.(sessionExpiresAt);
                };
                if (warnInMs === 0)
                    fireWarning();
                else
                    timers.push(setTimeout(fireWarning, warnInMs));
            }
            if (refreshInMs !== null) {
                if (refreshInMs === 0)
                    void refresh();
                else
                    timers.push(setTimeout(() => void refresh(), refreshInMs));
            }
            return () => {
                for (const timer of timers)
                    clearTimeout(timer);
            };
        }, [sessionExpiresAt, sessionExpiringSoon, isAuthenticated, refresh]);
        // --- Task 3.4: multi-tab synchronization -------------------------------
        useEffect(() => {
            const target = resolveStorageTarget();
            if (!target)
                return;
            const handler = (event) => {
                // storage.clear() fires with key === null; otherwise ignore keys
                // that aren't our access token.
                if (event.key !== null && event.key !== tokenStorageKey)
                    return;
                const token = options.tokenStorage.getToken();
                if (!token) {
                    // Logged out in another tab: local cleanup only (no API call).
                    // Forget any pending retry too, or it fires against no session.
                    failures.current = 0;
                    clearRetry();
                    options.onLogoutCleanup?.();
                    resetState();
                    return;
                }
                // Logged in / refreshed in another tab: re-hydrate this tab.
                options
                    .loadSession()
                    .then(succeed)
                    .catch((error) => {
                    if (sessionGone(error)) {
                        clearLocalSession();
                        return;
                    }
                    scheduleRetry(error, () => void hydrateRef.current(), true);
                });
            };
            target.addEventListener("storage", handler);
            return () => target.removeEventListener("storage", handler);
        }, [
            succeed,
            sessionGone,
            clearLocalSession,
            scheduleRetry,
            clearRetry,
            resetState,
        ]);
        // --- Task 3.3 (reactive): local logout for the transport layer ---------
        // A mid-session request whose token refresh is refused is a terminal 401
        // (an unreachable refresh is not: it keeps the session). The
        // API client routes that here via the registered unauthorized handler.
        // callApi:false so we never re-POST /auth/logout (that would 401 and loop
        // back through the same path); clearing state makes RequireAuth redirect.
        useEffect(() => {
            requestLocalLogout = () => {
                void performLogout({ callApi: false });
            };
            return () => {
                requestLocalLogout = null;
            };
        }, [performLogout]);
        const login = useCallback(async (email, password) => {
            setPartialState({ error: null, loading: true });
            try {
                const response = await options.login(email, password);
                options.tokenStorage.setToken(response.token);
                const session = options.hydrateAfterLogin
                    ? await options.hydrateAfterLogin(response)
                    : { user: response.user, ...initialExtraState };
                succeed(session);
            }
            catch (error) {
                const message = error instanceof Error ? error.message : "Login failed";
                setPartialState({ error: message, loading: false });
                throw error;
            }
        }, [succeed, initialExtraState, setPartialState]);
        const signup = useCallback(async (email, password, name) => {
            setPartialState({ error: null, loading: true });
            try {
                const response = await options.signup(email, password, name);
                options.tokenStorage.setToken(response.token);
                const session = options.hydrateAfterSignup
                    ? await options.hydrateAfterSignup(response)
                    : { user: response.user, ...initialExtraState };
                succeed(session);
            }
            catch (error) {
                const message = error instanceof Error ? error.message : "Signup failed";
                setPartialState({ error: message, loading: false });
                throw error;
            }
        }, [succeed, initialExtraState, setPartialState]);
        return {
            ...state,
            login,
            signup,
            logout,
            refresh,
            retrySession,
        };
    }
    function AuthProvider({ children }) {
        const auth = useAuthState();
        return _jsx(AuthContext.Provider, { value: auth, children: children });
    }
    function RequireAuth({ children, loadingFallback = null, unavailableFallback, redirectTo = "/login", }) {
        const auth = useAuth();
        const location = useLocation();
        if (auth.loading)
            return _jsx(_Fragment, { children: loadingFallback });
        if (!auth.user && auth.unavailable) {
            return (_jsx(_Fragment, { children: unavailableFallback
                    ? unavailableFallback({
                        retryAt: auth.unavailable.retryAt,
                        retry: auth.retrySession,
                    })
                    : loadingFallback }));
        }
        if (!auth.user) {
            return _jsx(Navigate, { to: redirectTo, state: { from: location }, replace: true });
        }
        return _jsx(_Fragment, { children: children });
    }
    return {
        AuthContext,
        AuthProvider,
        RequireAuth,
        useAuth,
        useAuthState,
        expireSession,
    };
}
export { decodeJwtPayload, getTokenExpiration, isTokenExpired } from "./jwt";
export { computeExpirySchedule, } from "./schedule";
export { isSessionGone, MAX_RETRY_MS, MIN_RETRY_MS, parseRetryAfter, RETRY_BACKOFF_MS, retryDelayMs, } from "./transient";
export { AuthApiError };
